import { BorderGridContainer, Button, GridBox, H1, TypeWrapper } from '@ahmdigital/ui';
import { flatMap, isEmpty } from 'lodash/fp';
import portableTextToPlain from '@ahmdigital/logic/lib/content/utils/portable-text-to-plain';

import { Article } from '../../types/__codegen__/sanity/graphql';
import { ARTICLE_HUB_FEATURE } from '../../ahm-constants/ui';
import {
  BackButton,
  BodyContent,
  BodyImage,
  Container,
  Description,
  InnerContainer,
  MainImage,
  RelativeContainer,
} from './styles';
import { useFeatureIsOn } from '../../../../utils/growth-book/hooks';
import ArticleMetadata from './components/article-metadata';
import buildArticleSchema from '../../components/structured-data/utils/build-article-schema';
import constants from '../../ahm-constants';
import getImageAttributes from '../../cms/utils/get-image-attributes';
import Head from '../../../utils/head';
import imageUrlBuilder from '../../cms/utils/image-url-builder';
import mapTermsAndConditions from '../../utils/map-terms-and-conditions';
import StructuredData from '../../components/structured-data';
import TermsAndConditionsComponent from '../../components/terms-and-conditions';
import TermsSection from '../../components/sections/terms-section';

type ImageNode = {
  _key: string;
  _type: string;
  asset: {
    _ref: string;
    _type: string;
  };
  caption: string;
};

const ImageSerializer = ({ node }: { node: ImageNode }) => {
  const { asset, caption } = node;
  const src = imageUrlBuilder(asset).withOptions({ width: undefined }).height(800).dpr(2).url();

  return src ? <BodyImage src={src} alt={caption || ''} /> : null;
};

type BlogDetailProps = {
  blogPost: Article | null | undefined;
};

const BlogDetail = ({ blogPost }: BlogDetailProps) => {
  const isFeatureEnabled = useFeatureIsOn(ARTICLE_HUB_FEATURE);

  if (!blogPost) {
    return null;
  }
  const mappedTerms = flatMap((terms) => mapTermsAndConditions(terms), blogPost.termsAndConditions);
  const { name, descriptionRaw, urlObject, bodyRaw, imageObject } = blogPost;
  const descriptionPlainText = portableTextToPlain(descriptionRaw);
  const mainImageAttributes = getImageAttributes({ height: 800, imageObject });

  return (
    <>
      <Head>
        <title>{name}</title>
        {descriptionPlainText && <meta name="description" content={descriptionPlainText} />}
      </Head>
      <StructuredData data={buildArticleSchema(blogPost)} />
      <BorderGridContainer hideBorderTop>
        <GridBox padding="0">
          <RelativeContainer>
            {isFeatureEnabled && (
              <BackButton icon={{ name: 'app-chevron-left' }} to={constants.URLS.SALES.ARTICLE_HUB}>
                Article hub
              </BackButton>
            )}
            <Container>
              <TypeWrapper variant="center">
                <H1 variant="marginBottomSpacer8">{name}</H1>
                <ArticleMetadata article={blogPost} />
                {mainImageAttributes && <MainImage {...mainImageAttributes} />}
              </TypeWrapper>
              <InnerContainer>
                {descriptionPlainText && (
                  <Description variant="marginBottomSpacer9" looksLike="h4">
                    {descriptionPlainText}
                  </Description>
                )}
                {urlObject?.url && urlObject.name && (
                  <Button to={urlObject.url} variant="marginBottomSpacer9">
                    {urlObject.name}
                  </Button>
                )}
                <BodyContent
                  source={bodyRaw}
                  typeSerializers={{
                    image: ImageSerializer,
                  }}
                />
              </InnerContainer>
            </Container>
          </RelativeContainer>
        </GridBox>
      </BorderGridContainer>
      {!isEmpty(mappedTerms) && <TermsSection termsComponent={<TermsAndConditionsComponent terms={mappedTerms} />} />}
    </>
  );
};

export default BlogDetail;
